'use strict';

/**
 * Horizontal Tab Slider with Scroll Spy
 * Makes navigation sticky and updates active tab based on scroll position
 * Only active on mobile screens (below 768px)
 */

// ========================================
// Constants
// ========================================
const MOBILE_BREAKPOINT = 576;
const DESKTOP_LARGE_BREAKPOINT = 1200;
const STICKY_TOP = 85;
const SCROLL_ANIMATION_DURATION = 1000;
const NAV_SCROLL_PADDING = 20;

// Store active slider instances
const activeSliders = new Map();
const wrappedContent = new WeakSet();

// ========================================
// Initialization
// ========================================

function initHorizontalTabSliders() {
  const tabSliders = document.querySelectorAll( '.e-tbs' );
  
  if ( !tabSliders.length ) return;
  
  tabSliders.forEach( ( slider ) => {
    setupTabContentPlayLinks( slider );
    handleSliderInitialization( slider );
    handleContentWrapping( slider );
  } );
  
  // Setup resize listener to handle screen size changes
  window.addEventListener( 'resize', debounce( handleResize, 250 ) );
}

function handleSliderInitialization( sliderContainer ) {
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  
  if ( isMobile ) {
    initSingleSlider( sliderContainer );
  } else {
    destroySingleSlider( sliderContainer );
  }
}

function handleResize() {
  const tabSliders = document.querySelectorAll( '.e-tbs' );
  
  tabSliders.forEach( ( slider ) => {
    setupTabContentPlayLinks( slider );
    handleSliderInitialization( slider );
    handleContentWrapping( slider );
  } );
}

function debounce( func, wait ) {
  let timeout;
  return function executedFunction() {
    const later = () => {
      clearTimeout( timeout );
      func();
    };
    clearTimeout( timeout );
    timeout = setTimeout( later, wait );
  };
}

function setupTabContentPlayLinks( sliderContainer ) {
  const tabContents = sliderContainer.querySelectorAll( '.e-div--tab-content' );

  tabContents.forEach( ( tabContent ) => {
    if ( tabContent.querySelector( '.e-vid' ) ) {
      tabContent.classList.remove( 'has-play-anchor' );
      return;
    }

    if ( tabContent.dataset.playAnchorBound === 'true' ) return;

    const playLink = tabContent.querySelector( '.e-img a[href]' );
    if ( !playLink ) return;

    tabContent.classList.add( 'has-play-anchor' );

    tabContent.addEventListener( 'click', ( e ) => {
      const interactiveEl = e.target.closest( 'a, button, input, textarea, select, label' );
      if ( interactiveEl ) return;
      playLink.click();
    } );

    tabContent.dataset.playAnchorBound = 'true';
  } );
}

// ========================================
// Content Wrapping (Desktop > 1200px)
// ========================================

function handleContentWrapping( sliderContainer ) {
  const isLargeDesktop = window.innerWidth >= DESKTOP_LARGE_BREAKPOINT;
  const content = sliderContainer.querySelector( '.e-tbs__content' );
  
  if ( !content ) return;
  
  const sliderContentDivs = content.querySelectorAll( '.e-div--slider-content' );
  
  sliderContentDivs.forEach( ( contentDiv ) => {
    if ( isLargeDesktop ) {
      wrapParagraphs( contentDiv );
    } else {
      unwrapParagraphs( contentDiv );
    }
  } );
}

function wrapParagraphs( contentDiv ) {
  // Check if already wrapped
  if ( wrappedContent.has( contentDiv ) ) return;
  
  // Get only direct p children (not nested ones)
  const paragraphs = Array.from( contentDiv.children ).filter( ( child ) => child.tagName === 'P' );
  
  if ( paragraphs.length === 0 ) return;
  
  // Create wrapper div
  const wrapper = document.createElement( 'div' );
  wrapper.className = 'e-div--text-wrapper';
  
  // Insert wrapper before first paragraph
  contentDiv.insertBefore( wrapper, paragraphs[0] );
  
  // Move all paragraphs into wrapper
  paragraphs.forEach( ( p ) => {
    wrapper.appendChild( p );
  } );
  
  // Mark as wrapped
  wrappedContent.add( contentDiv );
}

function unwrapParagraphs( contentDiv ) {
  // Check if not wrapped
  if ( !wrappedContent.has( contentDiv ) ) return;
  
  const wrapper = contentDiv.querySelector( '.e-div--text-wrapper' );
  
  if ( !wrapper ) {
    wrappedContent.delete( contentDiv );
    return;
  }
  
  const paragraphs = Array.from( wrapper.children ).filter( ( child ) => child.tagName === 'P' );
  
  // Move paragraphs back out of wrapper before the wrapper itself
  paragraphs.forEach( ( p ) => {
    contentDiv.insertBefore( p, wrapper );
  } );
  
  // Remove wrapper
  wrapper.remove();
  
  // Mark as unwrapped
  wrappedContent.delete( contentDiv );
}

// ========================================
// Helper Factories
// ========================================

function createVideoController( contentSlides ) {
  return {
    updatePlayback( activeIndex ) {
      contentSlides.forEach( ( slide, index ) => {
        const video = slide.querySelector( 'video' );
        if ( !video ) return;

        if ( index === activeIndex ) {
          video.play().catch( () => {} );
        } else {
          video.pause();
          video.currentTime = 0;
        }
      } );
    },
  };
}

function createNavScroller( nav ) {
  const navWrapper = nav.querySelector( '.swiper-wrapper' );

  function scrollLeft( activeSlideRect, navWrapperRect ) {
    navWrapper.scrollBy( {
      left: activeSlideRect.left - navWrapperRect.left - NAV_SCROLL_PADDING,
      behavior: 'smooth',
    } );
  }

  function scrollRight( activeSlideRect, navWrapperRect ) {
    navWrapper.scrollBy( {
      left: activeSlideRect.right - navWrapperRect.right + NAV_SCROLL_PADDING,
      behavior: 'smooth',
    } );
  }

  return {
    scrollIntoView( activeNavSlide ) {
      if ( !navWrapper ) return;

      const navWrapperRect = navWrapper.getBoundingClientRect();
      const activeSlideRect = activeNavSlide.getBoundingClientRect();

      if ( activeSlideRect.left < navWrapperRect.left ) {
        scrollLeft( activeSlideRect, navWrapperRect );
      } else if ( activeSlideRect.right > navWrapperRect.right ) {
        scrollRight( activeSlideRect, navWrapperRect );
      }
    },
  };
}

function createSlideActivator( navSlides, videoController, navScroller ) {
  return {
    activate( index ) {
      this.deactivateAll();

      if ( navSlides[index] ) {
        navSlides[index].classList.add( 'active' );
        navScroller.scrollIntoView( navSlides[index] );
        videoController.updatePlayback( index );
      }
    },

    deactivateAll() {
      navSlides.forEach( ( slide ) => slide.classList.remove( 'active' ) );
    },
  };
}

function createScrollDetector( section, nav, contentSlides ) {
  return {
    isSectionInView() {
      const sectionRect = section.getBoundingClientRect();
      return sectionRect.bottom >= 0;
    },

    findActiveSlideIndex() {
      const triggerPoint = STICKY_TOP + nav.offsetHeight + 50;

      for ( let i = contentSlides.length - 1; i >= 0; i-- ) {
        const rect = contentSlides[i].getBoundingClientRect();
        if ( rect.top <= triggerPoint ) {
          return i;
        }
      }

      return 0;
    },
  };
}

function createEventManager( state, eventHandlers, navSlides, contentSlides, nav, scrollDetector, slideActivator ) {
  function scrollToContentSlide( targetSlide ) {
    const targetRect = targetSlide.getBoundingClientRect();
    const offset = STICKY_TOP + nav.offsetHeight + 20;
    const targetPosition = window.scrollY + targetRect.top - offset;

    window.scrollTo( {
      top: targetPosition,
      behavior: 'smooth',
    } );
  }

  function handleNavClick( e, index ) {
    e.preventDefault();
    state.isScrolling = true;

    const targetSlide = contentSlides[index];
    if ( !targetSlide ) return;

    scrollToContentSlide( targetSlide );
    slideActivator.activate( index );

    setTimeout( () => {
      state.isScrolling = false;
    }, SCROLL_ANIMATION_DURATION );
  }

  function handleScroll() {
    if ( state.isScrolling ) return;
    if ( !scrollDetector.isSectionInView() ) return;

    const activeIndex = scrollDetector.findActiveSlideIndex();
    slideActivator.activate( activeIndex );
  }

  function handleScrollThrottle() {
    if ( state.ticking ) return;

    window.requestAnimationFrame( () => {
      handleScroll();
      state.ticking = false;
    } );
    state.ticking = true;
  }

  return {
    setupScrollListener() {
      eventHandlers.scroll = handleScrollThrottle;
      window.addEventListener( 'scroll', eventHandlers.scroll );
    },

    setupClickListeners() {
      navSlides.forEach( ( navSlide, index ) => {
        const clickHandler = ( e ) => handleNavClick( e, index );
        navSlide.addEventListener( 'click', clickHandler );
        eventHandlers.clicks.push( { element: navSlide, handler: clickHandler } );
      } );
    },
  };
}

function initSingleSlider( sliderContainer ) {
  if ( activeSliders.has( sliderContainer ) ) return;

  const nav = sliderContainer.querySelector( '.e-tbs__nav' );
  const content = sliderContainer.querySelector( '.e-tbs__content' );

  if ( !nav || !content ) return;

  const navSlides = Array.from( nav.querySelectorAll( '.swiper-slide' ) );
  const contentSlides = Array.from( content.querySelectorAll( '.swiper-slide' ) );

  if ( navSlides.length === 0 || contentSlides.length === 0 ) return;

  const section = sliderContainer.closest( '.e-stn--horizontal-tabslider' ) || sliderContainer;

  const state = {
    isScrolling: false,
    ticking: false,
  };

  const eventHandlers = {
    scroll: null,
    clicks: [],
  };

  // Create helper instances
  const videoController = createVideoController( contentSlides );
  const navScroller = createNavScroller( nav );
  const slideActivator = createSlideActivator( navSlides, videoController, navScroller );
  const scrollDetector = createScrollDetector( section, nav, contentSlides );
  const eventManager = createEventManager(
    state,
    eventHandlers,
    navSlides,
    contentSlides,
    nav,
    scrollDetector,
    slideActivator
  );

  // Initialize
  slideActivator.activate( 0 );
  eventManager.setupScrollListener();
  eventManager.setupClickListeners();

  activeSliders.set( sliderContainer, {
    eventHandlers,
    state,
  } );
}

function destroySingleSlider( sliderContainer ) {
  const instance = activeSliders.get( sliderContainer );
  
  if ( !instance ) return;
  
  const { eventHandlers } = instance;
  
  // Remove scroll listener
  if ( eventHandlers.scroll ) {
    window.removeEventListener( 'scroll', eventHandlers.scroll );
  }
  
  // Remove click listeners
  eventHandlers.clicks.forEach( ( { element, handler } ) => {
    element.removeEventListener( 'click', handler );
  } );
  
  // Remove active classes
  const nav = sliderContainer.querySelector( '.e-tbs__nav' );
  if ( nav ) {
    const navSlides = nav.querySelectorAll( '.swiper-slide' );
    navSlides.forEach( ( slide ) => slide.classList.remove( 'active' ) );
  }
  
  // Pause all videos
  const content = sliderContainer.querySelector( '.e-tbs__content' );
  if ( content ) {
    const videos = content.querySelectorAll( 'video' );
    videos.forEach( ( video ) => {
      video.pause();
      video.currentTime = 0;
    } );
  }
  
  // Remove from active sliders
  activeSliders.delete( sliderContainer );
}

// ========================================
// Bootstrap
// ========================================

if ( document.readyState === 'loading' ) {
  document.addEventListener( 'DOMContentLoaded', initHorizontalTabSliders );
} else {
  initHorizontalTabSliders();
}
